// src/stores/useMusicStore.ts
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import * as Tone from 'tone';
import { WebMidi } from 'webmidi';
import random from 'random';
import type { MidiDevice, MidiCC, DevicePattern } from '~/types';
import { NoteGenerator, type NoteGeneratorConfig } from '~/lib/audio/NoteGenerator';
import { MidiClock } from '~/lib/midi/MidiClock';
import { AudioMidiClock } from '~/lib/midi/AudioMidiClock';
import { AudioScheduler } from '~/lib/audio/AudioScheduler';
import { getLatencyConfig, getDefaultLatencyPreset, type LatencyPreset } from '~/lib/audio/latencyPresets';
import { EnvelopeGate } from '~/lib/audio/EnvelopeGate';
import { isMidiSupported, getMidiErrorMessage, getPlatform } from '~/lib/platform';
import { getDefaultHardwareProfile } from '~/lib/profiles/defaultHardwareProfile';
import { applyProfileToCCs } from '~/lib/profiles/applyProfile';
import { applyMultipleCCsToSynth, type SupportedSynth, type SynthFilter } from '~/lib/audio/ccMapping';
import { initializeOptimizedAudioContext, getAudioContext, isAudioWorkletSupported } from '~/lib/audio/audioContext';
import { ANTI_POP_FADE_IN_TIME, ANTI_POP_INITIAL_VOLUME, ANTI_POP_TARGET_VOLUME } from '~/lib/audio/constants';
import { getInitialCCs } from './useMidiDevicesStore';
import { useNotesStore } from './useNotesStore';
import { getDefaultDevicePattern } from '~/lib/patterns/defaultPattern';
import musicData from '~/data/musicData';
import { PATTERNS } from '~/types/pattern';
import { generatePatternParts } from '~/lib/audio/PatternGenerator';
import { loadDevicesFromStorage, saveDevicesToStorage as saveDevices, loadInitProjectDevices } from '~/lib/storage/localStorage';
import { VIRTUAL_SYNTHS, type VirtualSynthConfig } from '~/config/devices';
import { getCCPresetManager, getPatternPresetManager } from '~/lib/presets';
import { getDefaultMonoPattern } from '~/config/presets/patterns/default-mono-pattern';
import { getDefaultHardwarePattern } from '~/config/presets/patterns/default-hardware-pattern';
import { MidiService } from '~/lib/midi/midi-service';
import { WASMFMSynth } from '~/lib/wasm/wasm-synth-wrapper';
import { WASMMonoSynth } from '~/lib/wasm/wasm-mono-synth-wrapper';
import { FilterBridge } from '~/lib/wasm/filter-bridge';
import { EffectsBridge } from '~/lib/wasm/effects-bridge';
import { useEffectsStore } from './useEffectsStore';
import { noteNameToMidi, durationToSeconds } from '~/lib/audio/durationUtils';
import { applyKeyboardTracking } from '~/lib/audio/keyboardTracking';

interface MusicState {
  devices: MidiDevice[];
  initialized: boolean;
  midiError: string | null;
  synthReady: boolean;
  midiReady: boolean;
  isInitializing: boolean;
  currentNote: string | null;
  currentInfo: string;
  noteTrigger: number;
  midiClockEnabled: boolean;
  midiClockRunning: boolean;
  devicePatternPositions: Record<number, number>; // Map device index to pattern position
  currentPlaybackPosition: number; // Global playback position (shared during playback)
  deviceProgressPercentages: Record<number, number>; // Map device index to progress percentage (0-100)
  deviceTransitionDurations: Record<number, number>; // Map device index to transition duration in ms
  deviceCurrentNotes: Record<number, string | null>; // Map device index to current note being played
  globalBeatCounter: number; // Global beat count since Transport.start() (for beat-synchronized scrambling and events system)
  transportStartTime: number | null; // Transport start time in seconds (for calculating current beat)

  // Internal references (not serialized)
  synths: Record<number, SupportedSynth>; // Map of device index to synth instance
  filters: Record<number, SynthFilter>; // Map of device index to filter (Tone.Filter or FilterBridge)
  sharedReverb: Tone.Reverb | null; // Shared reverb for all virtual synths (legacy Tone.js)
  sharedEffects: EffectsBridge | null; // Shared WASM effects chain for all virtual synths
  noteGenerator: NoteGenerator | null; // Legacy - will be deprecated
  noteGenerators: Record<number, NoteGenerator>; // Map of device index to NoteGenerator
  beatCounterLoop: Tone.Loop | null; // Loop that increments globalBeatCounter on every quarter note
  midiClock: MidiClock | null;
  audioMidiClock: AudioMidiClock | null; // AudioScheduler-based MIDI clock (replaces setInterval)
  audioScheduler: AudioScheduler | null; // Shared AudioScheduler for timing
  latencyPreset: LatencyPreset; // User-facing latency setting
  envelopeGate: EnvelopeGate; // Anti-click envelope gate
  lastMidiNote: string | null; // Track last note sent to MIDI for retriggering
  midiService: MidiService | null; // Platform-agnostic MIDI service (CoreMIDI on iOS, WebMIDI on web)

  // Actions
  setLatencyPreset: (preset: LatencyPreset) => void;
  setDevices: (updater: (draft: MidiDevice[]) => void) => void;
  ensureSynthForDevice: (deviceIndex: number) => Promise<SupportedSynth>;
  getFilterForDevice: (deviceIndex: number) => SynthFilter | null;
  sendControlChanges: (device: MidiDevice) => void;
  toggleDevice: (deviceLabel: string) => void;
  toggleDevicePanel: (deviceIndex: number, open: boolean) => void;
  sendMidiNote: (note: string, duration: string, time?: number) => void;
  sendMidiNoteToDevice: (deviceIndex: number, note: string, duration: string) => void;
  setMidiClockEnabled: (enabled: boolean) => void;
  getDeviceUniqueId: (port: any) => string;
  handleDeviceConnected: (port: any) => void;
  handleDeviceDisconnected: (port: any) => void;
  updateDeviceList: () => void;
  initWebMidi: () => Promise<void>;
  startPlayback: (config: NoteGeneratorConfig, callbacks: any) => Promise<void>;
  stopPlayback: () => void;
  updateConfig: (config: NoteGeneratorConfig) => void;
  updateConfigContinuous: (config: NoteGeneratorConfig) => void;
  updateCallbacks: (callbacks: any) => void;
  clearCurrentNote: () => void;
  cleanup: () => void;
  // Pattern management actions
  setDevicePattern: (deviceIndex: number, updater: (pattern: DevicePattern) => void) => void;
  setDevicePatternPosition: (deviceIndex: number, position: number) => void;
  setDeviceProgressPercentage: (deviceIndex: number, percentage: number) => void;
  scrambleDevicePattern: (deviceIndex: number) => void;
  scrambleDeviceCCs: (deviceIndex: number) => Promise<void>;
  getDevicePattern: (deviceIndex: number) => DevicePattern | undefined;
  // Project save/load actions
  exportProject: () => string;
  importProject: (projectJson: string) => void;
}

// Use centralized localStorage module with sanitization
const loadPersistedDevices = loadDevicesFromStorage;
const saveDevicesToStorage = saveDevices;

/**
 * Get default pattern for a device based on its label/type
 * Uses pattern presets instead of JSON defaults
 */
function getDefaultPatternForDevice(label: string): DevicePattern {
  if (label === 'YO! Monosynth') {
    return getDefaultMonoPattern().pattern;
  } else if (label === 'YO! FM Synth') {
    // Force FM Synth to use pattern index 0 (new default from localStorage)
    return getDefaultDevicePattern(0);
  }
  // MIDI hardware devices use the hardware default pattern preset
  return getDefaultHardwarePattern().pattern;
}

/**
 * Get default active state for a device based on its type
 * Virtual synths: active = true (unmuted)
 * MIDI hardware: active = false (muted by default)
 */
function getDefaultActiveState(hasProductId: boolean): boolean {
  return !hasProductId; // Virtual synths (no productId) = true, MIDI hardware = false
}

/**
 * Create basic 128 CCs for hardware MIDI devices
 * Values are initialized to null (unknown state) until synced or scrambled
 */
function getBasicCCs(): MidiCC[] {
  return Array.from({ length: 128 }, (_, i) => ({
    number: i,
    value: null, // null = unknown state (not yet synced to hardware)
    scramble: false,
    smooth: false,
    show: false,
    min: 0,
    max: 127,
  }));
}

/**
 * Create a MidiDevice from a VirtualSynthConfig
 */
function createVirtualDevice(config: VirtualSynthConfig): MidiDevice {
  const ccPresetManager = getCCPresetManager();
  const patternPresetManager = getPatternPresetManager();

  // Determine device type for preset lookup
  const deviceType = config.synthType === 'FMSynth' ? 'FMSynth' as const : 'MonoSynth' as const;

  // Get default CC preset for this device type
  const ccPreset = ccPresetManager.getDefaultPresetForDeviceType(deviceType);

  console.log(`[PRESET LOADING] Device: ${config.label} (${deviceType})`);
  console.log(`[PRESET LOADING] Loading preset: ${ccPreset.id} - ${ccPreset.name}`);

  // Get default pattern preset
  let patternPresetId: string;
  if (config.synthType === 'FMSynth') {
    patternPresetId = 'default-fm-pattern';
  } else {
    patternPresetId = 'default-mono-pattern';
  }

  const patternPreset = patternPresetManager.getPattern(patternPresetId);

  // CRITICAL FIX: Use preset CC values instead of getInitialCCs()
  // The preset contains the correct values, not the generic initialization
  const initialCCs = getInitialCCs(config);

  // Merge preset CC values into the initial CCs array (all 128 CCs)
  // Preset CCs override the default 0 values
  ccPreset.cc.forEach(presetCC => {
    const existingCC = initialCCs.find(cc => cc.number === presetCC.number);
    if (existingCC) {
      existingCC.value = presetCC.value;
      existingCC.scramble = presetCC.scramble;
      existingCC.smooth = presetCC.smooth;
      existingCC.show = presetCC.show;
      existingCC.min = presetCC.min;
      existingCC.max = presetCC.max;
    }
  });

  const profileAppliedCCs = applyProfileToCCs(initialCCs, config.profile);

  // Create all 128 CCs object for debugging
  const allCCsObject: Record<number, number> = {};
  for (let i = 0; i < 128; i++) {
    const cc = profileAppliedCCs.find(c => c.number === i);
    allCCsObject[i] = cc?.value ?? 0;
  }

  console.log(`[PRESET LOADING] All 128 CC values (0-127) for ${config.label}:`);
  console.log(JSON.stringify(allCCsObject, null, 2));

  // Also log non-zero CCs for easier reading
  const nonZeroCCs = profileAppliedCCs
    .filter(cc => cc.value !== 0)
    .reduce((obj, cc) => ({ ...obj, [cc.number]: cc.value }), {});
  console.log(`[PRESET LOADING] Non-zero CC values for ${config.label}:`, nonZeroCCs);

  return {
    label: config.label,
    cc: profileAppliedCCs,
    profiles: [config.profile],
    activeProfileId: config.profile.id,
    state: "connected",
    enabled: true,
    active: getDefaultActiveState(false), // Virtual synths have no productId, so active = true
    panelOpen: false, // Virtual devices default to panel closed
    synthType: config.synthType,
    pattern: patternPreset?.pattern || getDefaultPatternForDevice(config.label),
    // Preset tracking
    ccPresetId: ccPreset.id,
    patternPresetId: patternPreset?.id || null,
  };
}

/**
 * Initialize virtual devices from VIRTUAL_SYNTHS config
 * If persisted devices exist, merge their settings
 * If no persisted devices, load from init.json (first launch or after reset)
 */
function initializeVirtualDevices(): MidiDevice[] {
  const persistedDevices = loadPersistedDevices();

  // FIRST LAUNCH / RESET: No localStorage, load from init.json
  if (!persistedDevices) {
    console.log('[INIT] No persisted devices found, loading from init.json');
    const initDevices = loadInitProjectDevices();

    return initDevices.map((device, index) => {
      // Ensure device has all required fields
      const config = VIRTUAL_SYNTHS[index];
      if (!config) {
        console.error(`[INIT] No config found for device at index ${index}`);
        return createVirtualDevice(VIRTUAL_SYNTHS[0]); // Fallback to first config
      }

      // Migration: Add missing fields
      const finalDevice = {
        ...device,
        enabled: true, // Always true for virtual devices
        active: device.active ?? true, // Default to active
        panelOpen: device.panelOpen ?? true, // Default to open
        synthType: device.synthType || config.synthType,
        pattern: device.pattern || getDefaultDevicePattern(),
        cc: device.cc || [],
        profiles: device.profiles || [],
        activeProfileId: device.activeProfileId || 'default',
      } as MidiDevice;

      return finalDevice;
    });
  }

  // PERSISTED DEVICES: Merge with config
  return VIRTUAL_SYNTHS.map((config) => {
    // Try to find persisted device with same label
    const persistedDevice = persistedDevices?.find((d: any) => d.label === config.label);

    if (persistedDevice) {
      // Merge persisted settings with config
      // CRITICAL: Only create fallback device if CC data is actually missing
      // This prevents overwriting saved CC values with defaults on every load
      const needsFallback = !persistedDevice.cc || !persistedDevice.profiles || !persistedDevice.activeProfileId;

      const fallback = needsFallback ? createVirtualDevice(config) : null;

      const finalDevice = {
        ...persistedDevice,
        enabled: true, // Always true for virtual devices
        active: true, // Force virtual synths to be unmuted (migration from old enabled state)
        panelOpen: persistedDevice.panelOpen ?? true, // Preserve panelOpen or default to true
        synthType: config.synthType, // Ensure synthType is set
        pattern: persistedDevice.pattern || getDefaultDevicePattern(), // Migration: add pattern if missing
        // Explicitly preserve CC array (critical for persistence) - only use fallback if missing
        cc: persistedDevice.cc || fallback?.cc || [],
        profiles: persistedDevice.profiles || fallback?.profiles || [],
        activeProfileId: persistedDevice.activeProfileId || fallback?.activeProfileId || 'default',
        // Preserve preset IDs if they exist, otherwise leave undefined for migration
        ccPresetId: persistedDevice.ccPresetId,
        patternPresetId: persistedDevice.patternPresetId,
      } as MidiDevice;

      return finalDevice;
    }

    // No persisted device - create new from config
    return createVirtualDevice(config);
  });
}

// Store Tone.js objects outside Immer state to avoid proxy issues
let sharedReverbInstance: Tone.Reverb | null = null;
let sharedCompressorInstance: Tone.Compressor | null = null;
let sharedLimiterInstance: Tone.Limiter | null = null;

// WASM effects chain (used when AudioWorklet is supported)
let sharedWASMEffects: EffectsBridge | null = null;
let wasmGainNode: GainNode | null = null; // Final gain before destination

// Session-level synth mode - determined once at first synth creation
// Ensures ALL synths use the same implementation to avoid phase issues
let sessionSynthMode: 'wasm' | 'tonejs' | null = null;

export const useMusicStore = create<MusicState>()(
  immer((set, get) => {
    // Initialize virtual devices from config
    const virtualDevices = initializeVirtualDevices();

    // Initialize device positions/progress for all virtual devices
    const initialDevicePositions: Record<number, number> = {};
    const initialDeviceProgress: Record<number, number> = {};
    const initialDeviceTransitionDurations: Record<number, number> = {};
    const initialDeviceCurrentNotes: Record<number, string | null> = {};

    virtualDevices.forEach((_, index) => {
      initialDevicePositions[index] = 0;
      initialDeviceProgress[index] = 0;
      initialDeviceTransitionDurations[index] = 250;
      initialDeviceCurrentNotes[index] = null;
    });

    return {
      devices: virtualDevices,
      initialized: false,
      midiError: null,
      synthReady: false,
      midiReady: false,
      isInitializing: true,
      currentNote: null,
      currentInfo: "",
      noteTrigger: 0,
      midiClockEnabled: false,
      midiClockRunning: false,
      devicePatternPositions: initialDevicePositions,
      currentPlaybackPosition: 0,
      deviceProgressPercentages: initialDeviceProgress,
      deviceTransitionDurations: initialDeviceTransitionDurations,
      deviceCurrentNotes: initialDeviceCurrentNotes,
      globalBeatCounter: 0,
      transportStartTime: null,

      synths: {}, // Map of device index to synth instance
      filters: {}, // Map of device index to filter
      noteGenerator: null,
      noteGenerators: {},
      beatCounterLoop: null,
      midiClock: null,
      audioMidiClock: null,
      audioScheduler: null,
      latencyPreset: getDefaultLatencyPreset(),
      envelopeGate: new EnvelopeGate(),
      lastMidiNote: null,
      sharedReverb: null, // Not used - actual reverb stored in module variable
      sharedEffects: null, // WASM effects chain
      midiService: null, // Platform-agnostic MIDI service

      setDevices: (updater) =>
        set((draft) => {
          updater(draft.devices);
          console.log('[STORAGE] Saving devices:', draft.devices.length, 'devices');
          saveDevicesToStorage(draft.devices);
        }),

      ensureSynthForDevice: async (deviceIndex: number) => {
        const state = get();
        console.log(`[SYNTH] ensureSynthForDevice called for device ${deviceIndex}`);
        console.log(`[TONE.JS] Context state:`, Tone.getContext().state);

        // Initialize audio context with platform-optimized settings if not already configured
        // This ensures audio quality is optimized even if ensureSynthForDevice is called
        // before the user clicks Play (defensive initialization)
        await initializeOptimizedAudioContext();

        // Determine session synth mode ONCE on first synth creation
        // All synths in session use the same mode to prevent phase issues
        if (sessionSynthMode === null) {
          const audioWorkletAvailable = typeof AudioWorkletNode !== 'undefined';
          sessionSynthMode = audioWorkletAvailable ? 'wasm' : 'tonejs';
          console.log(`[SYNTH] Session synth mode determined: ${sessionSynthMode}`);
        }

        console.log(`[TONE.JS] Destination:`, Tone.getDestination());
        console.log(`[TONE.JS] Sample rate:`, Tone.getContext().sampleRate);

        // Return existing synth if already created
        if (state.synths[deviceIndex]) {
          console.log(`[SYNTH] Synth already exists for device ${deviceIndex}, returning existing`);
          return state.synths[deviceIndex];
        }

        const device = state.devices[deviceIndex];
        if (!device) {
          console.error(`[SYNTH] Device ${deviceIndex} not found!`);
          throw new Error(`Device ${deviceIndex} not found`);
        }

        console.log(`[SYNTH] Creating new synth for device ${deviceIndex}: ${device.label}`);

        // Create shared master effects chain ONCE for all virtual synths
        // Audio flow: Synth → Filter → Reverb → Compressor → Limiter → Destination

        // Create shared limiter (final safety ceiling, prevents clipping)
        if (!sharedLimiterInstance) {
          try {
            console.log(`[SYNTH] Creating shared limiter (-3dB threshold)`);
            // More aggressive limiting at -3dB to prevent any clipping
            sharedLimiterInstance = new Tone.Limiter(-3).toDestination();
            console.log(`[SYNTH] Shared limiter created`);

            // Connect recorder to limiter output for capturing final audio
            try {
              const { getAudioRecorder } = await import('~/lib/audio/AudioRecorder');
              const recorder = getAudioRecorder();
              recorder.connectToOutput(sharedLimiterInstance);
              console.log(`[SYNTH] Audio recorder connected to output chain`);
            } catch (error) {
              console.warn(`[SYNTH] Failed to connect recorder:`, error);
            }

            // Connect analyzer for audio visualization
            try {
              const { getAudioAnalyzer } = await import('~/lib/audio/AudioAnalyzer');
              const analyzer = getAudioAnalyzer();
              analyzer.connectToOutput(sharedLimiterInstance);
              console.log(`[SYNTH] Audio analyzer connected to output chain`);
            } catch (error) {
              console.warn(`[SYNTH] Failed to connect analyzer:`, error);
            }
          } catch (error) {
            console.warn(`[SYNTH] Failed to create limiter (test environment?), connecting directly to destination:`, error);
            // In test environments, Tone.js audio context may not be fully initialized
            // Fall back to using Tone.Destination directly
          }
        }

        // Create shared compressor (glues mix together, controls dynamics)
        if (!sharedCompressorInstance && sharedLimiterInstance) {
          try {
            console.log(`[SYNTH] Creating shared compressor`);
            sharedCompressorInstance = new Tone.Compressor({
              threshold: -12,  // More aggressive threshold to catch peaks earlier
              ratio: 6,        // Higher ratio for stronger compression
              attack: 0.002,   // Very fast attack (2ms) to catch transients
              release: 0.25,   // Slower release (250ms) for smoother dynamics
              knee: 2.5,       // Soft knee for more musical compression
            }).connect(sharedLimiterInstance);
            console.log(`[SYNTH] Shared compressor created (threshold: -12dB, ratio: 6:1)`);
          } catch (error) {
            console.warn(`[SYNTH] Failed to create compressor (test environment?):`, error);
          }
        }

        // Create shared reverb (adds space and glue)
        // Connect to: compressor if available, else limiter if available, else destination
        if (!sharedReverbInstance) {
          console.log(`[SYNTH] Creating shared reverb`);
          const reverb = new Tone.Reverb({
            decay: 1.5,  // Slightly shorter decay to reduce buildup
            wet: 0.15,   // Reduced wet mix to prevent gain buildup
            preDelay: 0.01, // Small pre-delay for clarity
          });

          // Connect to the appropriate target
          if (sharedCompressorInstance) {
            reverb.connect(sharedCompressorInstance);
          } else if (sharedLimiterInstance) {
            reverb.connect(sharedLimiterInstance);
          } else {
            reverb.toDestination();
          }

          sharedReverbInstance = reverb;
          console.log(`[SYNTH] Shared reverb created (wet: 0.25, decay: 2.0s)`);
        }

        // Create filter for this device (warm settings)
        console.log(`[SYNTH] Creating filter for device ${deviceIndex}`);
        const filter = new Tone.Filter({
          frequency: 1500, // Slightly warmer default
          type: "lowpass",
          rolloff: -24,
          Q: 0.5, // Very low resonance for warmth
        });

        // Set up effects chain for this device (with graceful fallback for tests)
        try {
          const effectsStore = useEffectsStore.getState();
          const effectsManager = effectsStore.effectsManager;

          // Create effects chain if it doesn't exist
          if (!effectsManager.getEffectsChain(deviceIndex)) {
            effectsManager.createEffectsChain(deviceIndex);
          }

          // Get the input and output nodes for the effects chain
          const effectsInput = effectsManager.getInputNode(deviceIndex);
          const effectsOutput = effectsManager.getOutputNode(deviceIndex);

          if (effectsInput && effectsOutput) {
            // Connect filter → effects chain → reverb
            filter.connect(effectsInput);
            effectsOutput.disconnect(); // Disconnect from default destination
            effectsOutput.connect(sharedReverbInstance);
            console.log(`[SYNTH] Audio chain: Filter → Effects Chain → Reverb for device ${deviceIndex}`);
          } else {
            // Fallback: connect directly to reverb if effects chain fails
            filter.connect(sharedReverbInstance);
            console.log(`[SYNTH] Filter connected directly to reverb for device ${deviceIndex} (effects chain not available)`);
          }
        } catch (error) {
          // In test environment or if effects fail, connect directly
          filter.connect(sharedReverbInstance);
          console.log(`[SYNTH] Filter connected directly to reverb for device ${deviceIndex} (effects system unavailable)`);
        }

        console.log(`[SYNTH] Filter created for device ${deviceIndex}`);


        // Create synth based on device synthType
        let synth: SupportedSynth;

        if (device.synthType === "FMSynth") {
          // Use session synth mode (determined once for consistency)
          if (sessionSynthMode === 'wasm') {
            console.log(`[SYNTH] Creating WASM FMSynth for device ${deviceIndex}`);
            try {
              const toneContext = Tone.getContext();
              const audioContext = (toneContext.rawContext || toneContext) as AudioContext;
              const wasmSynth = new WASMFMSynth(audioContext);
              await wasmSynth.initialize();

              // Set initial FM synthesis parameters (warm settings for multi-synth playback)
              // harmonicity 2.0 (octave) creates even harmonics that don't clash with triangle's odd harmonics
              wasmSynth.wasmBridge.setHarmonicity(2.0);
              wasmSynth.wasmBridge.setModulationIndex(3.0); // Gentle FM depth for clean harmonics
              wasmSynth.wasmBridge.setCarrierType(0); // Sine
              wasmSynth.wasmBridge.setModulatorType(0); // Sine
              wasmSynth.wasmBridge.setCarrierEnvelope(0.03, 0.2, 0.86, 1.0); // High sustain
              wasmSynth.wasmBridge.setModulationEnvelope(0.03, 0.25, 0.3, 0.8);
              wasmSynth.detune.value = 0; // No detuning - prevents beat frequency buzzing at low pitches

              synth = wasmSynth.connect(filter);
              console.log(`[SYNTH] WASM FMSynth created, initialized and connected to filter`);
            } catch (error) {
              console.warn(`[SYNTH] Failed to create WASM FMSynth, falling back to Tone.js FMSynth:`, error);
              // Fall through to Tone.js FMSynth creation below (warm settings)
              synth = new Tone.FMSynth({
                harmonicity: 2.0, // Octave - even harmonics don't clash with triangle's odd harmonics
                modulationIndex: 3.0, // Gentle FM depth for clean harmonics
                oscillator: { type: "sine" },
                envelope: { attack: 0.03, decay: 0.2, sustain: 0.86, release: 1.0 },
                modulation: { type: "sine" },
                modulationEnvelope: { attack: 0.03, decay: 0.25, sustain: 0.3, release: 0.8 },
                volume: -6,
                detune: 0, // No detuning - prevents beat frequency buzzing at low pitches
              }).connect(filter);
            }
          } else {
            // Session is using Tone.js synths (WASM not available) - warm settings
            console.log(`[SYNTH] Session mode is Tone.js, creating Tone.js FMSynth for device ${deviceIndex}`);
            synth = new Tone.FMSynth({
              harmonicity: 2.0, // Octave - even harmonics don't clash with triangle's odd harmonics
              modulationIndex: 3.0, // Gentle FM depth for clean harmonics
              oscillator: { type: "sine" },
              envelope: { attack: 0.03, decay: 0.2, sustain: 0.86, release: 1.0 },
              modulation: { type: "sine" },
              modulationEnvelope: { attack: 0.03, decay: 0.25, sustain: 0.3, release: 0.8 },
              volume: -6,
              detune: 0, // No detuning - prevents beat frequency buzzing at low pitches
            }).connect(filter);
            console.log(`[SYNTH] Tone.js FMSynth created, connecting to filter`);
          }
        } else {
          // Default to MonoSynth
          // Use session synth mode (determined once for consistency)
          if (sessionSynthMode === 'wasm') {
            console.log(`[SYNTH] Creating WASM MonoSynth for device ${deviceIndex}`);
            try {
              const toneContext = Tone.getContext();
              const audioContext = (toneContext.rawContext || toneContext) as AudioContext;
              const wasmSynth = new WASMMonoSynth(audioContext);
              await wasmSynth.initialize();

              // Set initial MonoSynth parameters (warm settings for multi-synth playback)
              // Triangle wave has softer harmonics - avoids clashing with FM synth
              wasmSynth.setOscillatorType(3); // Triangle
              wasmSynth.setAmpEnvelope(0.03, 0.35, 0.35, 0.9); // Softer attack
              wasmSynth.setFilterEnvelope(0.01, 0.5, 0.15, 0.9); // Smoother filter movement
              wasmSynth.setFilterCutoff(1500); // Slightly warmer
              wasmSynth.setFilterResonance(0.5); // Very low resonance for warmth
              wasmSynth.volume.value = -6;
              wasmSynth.detune.value = 0; // No detuning - prevents beat frequency buzzing at low pitches

              synth = wasmSynth.connect(filter);
              console.log(`[SYNTH] WASM MonoSynth created, initialized and connected to filter`);
            } catch (error) {
              console.warn(`[SYNTH] Failed to create WASM MonoSynth, falling back to Tone.js MonoSynth:`, error);
              // Fall through to Tone.js MonoSynth creation below (warm settings)
              synth = new Tone.MonoSynth({
                oscillator: { type: "triangle" }, // Softer harmonics - avoids clashing with FM
                envelope: {
                  attack: 0.03,
                  decay: 0.35,
                  sustain: 0.35,
                  release: 0.9,
                },
                volume: -6,
                detune: 0, // No detuning - prevents beat frequency buzzing at low pitches
                filterEnvelope: {
                  attack: 0.01,
                  decay: 0.5,
                  sustain: 0.15,
                  release: 0.9,
                  baseFrequency: 400, // Warmer base
                  octaves: 3, // Less extreme filter sweep
                },
              }).connect(filter);
            }
          } else {
            // Session is using Tone.js synths (WASM not available) - warm settings
            console.log(`[SYNTH] Session mode is Tone.js, creating Tone.js MonoSynth for device ${deviceIndex}`);
            synth = new Tone.MonoSynth({
              oscillator: { type: "triangle" }, // Softer harmonics - avoids clashing with FM
              envelope: {
                attack: 0.03,
                decay: 0.35,
                sustain: 0.35,
                release: 0.9,
              },
              volume: -6,
              detune: 0, // No detuning - prevents beat frequency buzzing at low pitches
              filterEnvelope: {
                attack: 0.01,
                decay: 0.5,
                sustain: 0.15,
                release: 0.9,
                baseFrequency: 400, // Warmer base
                octaves: 3, // Less extreme filter sweep
              },
            }).connect(filter);
            console.log(`[SYNTH] Tone.js MonoSynth created, connecting to filter`);
          }
        }

        console.log(`[SYNTH] Audio chain: ${device.synthType || 'MonoSynth'} → Filter → Effects Chain → Reverb → Compressor → Limiter → Destination`);
        console.log(`[SYNTH] Synth volume:`, synth.volume.value);

        // Store synth and filter
        set((draft) => {
          draft.synths[deviceIndex] = synth;
          draft.filters[deviceIndex] = filter;
          draft.synthReady = true;
        });

        console.log(`[SYNTH] ${device.synthType || 'MonoSynth'} initialized for device ${deviceIndex} (${device.label})`);
        console.log(`[SYNTH] synthReady set to true`);

        // Apply persisted CC values to the synth immediately after creation
        if (device.cc && device.cc.length > 0) {
          // Filter out CCs with null values (unknown state)
          const knownCCs = device.cc.filter((cc): cc is MidiCC & { value: number } => cc.value !== null);
          if (knownCCs.length > 0) {
            console.log(`[SYNTH] Applying ${knownCCs.length} persisted CC values to synth`);
            applyMultipleCCsToSynth(knownCCs, synth, filter);
            console.log(`[SYNTH] CC values applied to ${device.label}`);
          }
        }

        return synth;
      },

      getFilterForDevice: (deviceIndex: number) => get().filters[deviceIndex] || null,

      sendControlChanges: (device: MidiDevice) => {
        // const state = get(); // Unused but kept for potential future use

        if (!device || !device.enabled) return;
        if (WebMidi.outputs.length === 0) return;
        if (!device.productId) return;

        const output = WebMidi.outputs.find((o) => o.id === device.productId);
        if (!output) return;

        try {
          let sentCount = 0;
          device.cc.forEach((cc) => {
            // Only send CCs that are scrambled and have known values (not null)
            if (cc.scramble && cc.value !== null) {
              output.sendControlChange(cc.number, cc.value);
              sentCount++;
            }
          });
          console.log(`[MIDI] ✅ Sent ${sentCount} CCs to ${device.label}`);
        } catch (err) {
          console.error(`[MIDI] ❌ Failed to send CCs to ${device.label}`, err);
        }
      },

      toggleDevice: (deviceLabel: string) => {
        set((draft) => {
          const device = draft.devices.find((d: MidiDevice) => d.label === deviceLabel);
          if (device) {
            const newState = !device.active;
            device.active = newState;
            console.log(`🔘 [TOGGLE] Device: ${deviceLabel} → ${newState ? 'ACTIVE' : 'INACTIVE (MIDI muted)'}`);
            saveDevicesToStorage(draft.devices);
          }
        });
      },
      toggleDevicePanel: (deviceIndex: number, open: boolean) => {
        set((draft) => {
          const device = draft.devices[deviceIndex];
          if (device) {
            device.panelOpen = open;
            console.log(`📂 [PANEL] Device ${device.label} panel → ${open ? 'OPEN' : 'CLOSED'}`);
            saveDevicesToStorage(draft.devices);
          }
        });
      },

      sendMidiNote: (note: string, duration: string, _time?: number) => {
        const state = get();

        // If playing the same note as last time, stop it first to retrigger the envelope
        if (state.lastMidiNote === note) {
          state.devices.forEach((device) => {
            if (!device.enabled || !device.productId) return;
            const output = WebMidi.outputs.find((o) => o.id === device.productId);
            if (!output) return;

            try {
              output.stopNote(note);
            } catch (err) {
              // Ignore errors - note might not be playing
            }
          });
        }

        // Now play the new note
        state.devices.forEach((device) => {
          if (!device.enabled || !device.productId) return;

          const output = WebMidi.outputs.find((o) => o.id === device.productId);
          if (!output) return;

          try {
            // Convert duration using native utilities (default to 120 BPM for legacy calls)
            const durationMs = durationToSeconds(duration, 120) * 1000;
            output.playNote(note, { duration: durationMs });
          } catch (err) {
            console.error(`Failed to send note to ${device.label}`, err);
          }
        });

        // Track the last note sent
        set({ lastMidiNote: note });
      },

      sendMidiNoteToDevice: (deviceIndex: number, note: string, duration: string) => {
        const state = get();
        const device = state.devices[deviceIndex];

        // Check enabled (device connected), active (user toggle), and productId (MIDI device)
        if (!device || !device.enabled || !device.productId) return;
        if (device.active === false) return; // MIDI mute - pattern plays but no notes sent

        const platform = getPlatform();
        // Convert duration using native utilities (default to 120 BPM for legacy calls)
        const durationMs = durationToSeconds(duration, 120) * 1000;

        // Convert note name (e.g., "G#2") to MIDI note number (0-127)
        const midiNoteNumber = noteNameToMidi(note);
        const channel = device.channel || 0;
        const velocity = 100; // Default velocity

        if (platform === 'ios' && state.midiService && device.productId) {
          // iOS: Use CoreMIDI via midiService
          try {
            const deviceId = device.productId;
            state.midiService.sendNoteOn(deviceId, channel, midiNoteNumber, velocity);

            // Schedule note off after duration
            setTimeout(() => {
              state.midiService?.sendNoteOff(deviceId, channel, midiNoteNumber, 0);
            }, durationMs);
          } catch (err) {
            console.error(`[iOS] Failed to send note to ${device.label}`, err);
          }
        } else {
          // Web: Use WebMIDI
          const output = WebMidi.outputs.find((o) => o.id === device.productId);
          if (!output) return;

          try {
            output.playNote(note, { duration: durationMs });
          } catch (err) {
            console.error(`Failed to send note to ${device.label}`, err);
          }
        }
      },

      setMidiClockEnabled: (enabled: boolean) => set({ midiClockEnabled: enabled }),

      setLatencyPreset: (preset: LatencyPreset) => {
        const config = getLatencyConfig(preset);
        const state = get();
        set({ latencyPreset: preset });

        // Update AudioScheduler lookAhead if running
        if (state.audioScheduler) {
          // AudioScheduler lookAhead is updated via constructor options
          // For live changes, we update the MIDI clock latency
          if (state.audioMidiClock) {
            state.audioMidiClock.setLatency(config.midiLatencyMs);
          }
        }
        console.log(`[LATENCY] Preset changed to "${preset}" (lookAhead: ${config.lookAheadSeconds}s, midiLatency: ${config.midiLatencyMs}ms)`);
      },

      // Helper function to generate a stable unique ID for a device
      getDeviceUniqueId: (port: any): string => {
        return `${port.manufacturer || 'unknown'}-${port.name || 'unknown'}`;
      },

      // Handle device connected event
      handleDeviceConnected: (port: any) => {
        if (port.type !== "output" || !port.channels || port.channels.length === 0) return;

        const uniqueId = get().getDeviceUniqueId(port);

        set((draft) => {
          const existingIndex = draft.devices.findIndex((d) => d.uniqueId === uniqueId);

          if (existingIndex !== -1) {
            // Device reconnected - update state and flash green
            draft.devices[existingIndex].state = "connected";
            draft.devices[existingIndex].productId = port.id;
            draft.devices[existingIndex].flashState = "green";

            // Clear flash after 250ms
            setTimeout(() => {
              set((draft) => {
                if (draft.devices[existingIndex]) {
                  draft.devices[existingIndex].flashState = "none";
                }
              });
            }, 250);
          } else {
            // Check if we have persisted settings for this device
            const persistedDevices = loadPersistedDevices();
            const persistedDevice = persistedDevices?.find((d: any) => d.uniqueId === uniqueId);

            // If no persisted device, apply default hardware profile
            let deviceCCs;
            let deviceProfiles;
            let deviceActiveProfileId;
            let devicePattern;

            if (persistedDevice && persistedDevice.cc) {
              // Use persisted settings
              deviceCCs = persistedDevice.cc;
              deviceProfiles = persistedDevice.profiles || [];
              deviceActiveProfileId = persistedDevice.activeProfileId || "";
              devicePattern = persistedDevice.pattern || getDefaultPatternForDevice('MIDI');
            } else {
              // Apply default hardware profile to initial CCs
              const hardwareProfile = getDefaultHardwareProfile();
              const initialCCs = getBasicCCs();
              deviceCCs = applyProfileToCCs(initialCCs, hardwareProfile);
              deviceProfiles = [hardwareProfile];
              deviceActiveProfileId = hardwareProfile.id;
              // Use default MIDI pattern for new hardware devices
              devicePattern = getDefaultPatternForDevice('MIDI');
            }

            // New device - add with green flash
            const newDevice: MidiDevice = {
              label: port.name,
              cc: deviceCCs,
              profiles: deviceProfiles,
              activeProfileId: deviceActiveProfileId,
              productId: port.id,
              uniqueId: uniqueId,
              state: "connected",
              enabled: persistedDevice?.enabled ?? true,
              active: persistedDevice?.active ?? getDefaultActiveState(true), // MIDI devices (with productId) default to muted
              panelOpen: persistedDevice?.panelOpen ?? false, // New devices default to panel closed
              flashState: "green",
              pattern: devicePattern,
            };

            // Prepend new device to the beginning of the list
            draft.devices.unshift(newDevice);

            // Update NoteGenerator indices since we prepended
            // Shift all existing NoteGenerator indices up by 1
            const oldNoteGenerators = { ...draft.noteGenerators };
            draft.noteGenerators = {};
            Object.entries(oldNoteGenerators).forEach(([index, noteGen]) => {
              const oldIndex = parseInt(index, 10);
              draft.noteGenerators[oldIndex + 1] = noteGen;
            });

            // Clear flash after 250ms
            setTimeout(() => {
              set((draft) => {
                const index = draft.devices.findIndex((d) => d.uniqueId === uniqueId);
                if (index !== -1) {
                  draft.devices[index].flashState = "none";
                }
              });
            }, 250);

            // Save the updated devices list
            saveDevicesToStorage(draft.devices);
          }
        });
      },

      // Handle device disconnected event
      handleDeviceDisconnected: (port: any) => {
        if (port.type !== "output") return;

        const uniqueId = get().getDeviceUniqueId(port);
        console.log(`[DEVICE DISCONNECT] ${port.name} (${uniqueId})`);

        const state = get();
        const existingIndex = state.devices.findIndex((d) => d.uniqueId === uniqueId);

        if (existingIndex !== -1) {
          // Stop and dispose the NoteGenerator for this device if it exists
          const noteGen = state.noteGenerators[existingIndex];
          if (noteGen) {
            console.log(`[DEVICE DISCONNECT] Stopping NoteGenerator for device ${existingIndex}`);
            noteGen.stop();
            noteGen.dispose();

            // Remove the NoteGenerator from the store
            set((draft) => {
              delete draft.noteGenerators[existingIndex];
            });
          }

          // Mark device as disconnected and flash red
          set((draft) => {
            draft.devices[existingIndex].state = "disconnected";
            draft.devices[existingIndex].enabled = false;
            draft.devices[existingIndex].productId = undefined; // Clear productId as device is no longer connected
            draft.devices[existingIndex].flashState = "red";

            // Clear flash after 250ms
            setTimeout(() => {
              set((draft) => {
                if (draft.devices[existingIndex]) {
                  draft.devices[existingIndex].flashState = "none";
                }
              });
            }, 250);
          });
        }
      },

      // Update the device list based on connected MIDI outputs (initial load)
      updateDeviceList: () => {

        const persistedDevices = loadPersistedDevices();
        const realDevices: MidiDevice[] = [];

        WebMidi.outputs.forEach((output) => {
          if (output.state === "connected" && output.channels.length > 0) {
            const uniqueId = get().getDeviceUniqueId(output);
            const persistedDevice = persistedDevices?.find((d: any) => d.uniqueId === uniqueId);

            // Check if device needs profile migration (has no CCs enabled)
            const needsProfileMigration = persistedDevice &&
              persistedDevice.cc &&
              !persistedDevice.cc.some((cc: any) => cc.scramble === true);

            let deviceCCs;
            let deviceProfiles;
            let deviceActiveProfileId;
            let devicePattern;

            if (persistedDevice && !needsProfileMigration && persistedDevice.cc) {
              // Use persisted settings
              deviceCCs = persistedDevice.cc;
              deviceProfiles = persistedDevice.profiles || [];
              deviceActiveProfileId = persistedDevice.activeProfileId || "";
              devicePattern = persistedDevice.pattern || getDefaultPatternForDevice('MIDI');
            } else {
              // Apply default hardware profile to initial CCs
              const hardwareProfile = getDefaultHardwareProfile();
              const initialCCs = getBasicCCs();
              deviceCCs = applyProfileToCCs(initialCCs, hardwareProfile);
              deviceProfiles = [hardwareProfile];
              deviceActiveProfileId = hardwareProfile.id;
              devicePattern = getDefaultPatternForDevice('MIDI');
            }

            // Migration: If active is undefined but enabled was false (old data), migrate to active: false
            // enabled should always be true for connected devices (it's auto-managed)
            // For new devices (no persisted data), default to false (muted)
            const migratedActive = persistedDevice?.active ??
                                   (persistedDevice?.enabled === false ? false : getDefaultActiveState(true));

            const device: MidiDevice = {
              label: output.name,
              cc: deviceCCs,
              profiles: deviceProfiles,
              activeProfileId: deviceActiveProfileId,
              productId: output.id,
              uniqueId: uniqueId,
              state: "connected",
              enabled: true, // Always true for connected devices
              active: migratedActive, // Migrated from old enabled value, or default to muted for new devices
              panelOpen: persistedDevice?.panelOpen ?? false, // Preserve panelOpen or default to closed for new devices
              flashState: "none",
              pattern: devicePattern,
            };
            realDevices.push(device);
          }
        });

        set((draft) => {
          // Keep all virtual synths (those with synthType and no productId)
          const virtualSynths = draft.devices.filter(d => d.synthType && !d.productId);

          // Set devices: virtual synths + real devices
          draft.devices = [...virtualSynths, ...realDevices];

          // Save to persist the migrated devices
          saveDevicesToStorage(draft.devices);
        });

      },

      initWebMidi: async () => {
        set({ midiReady: false });

        const platform = getPlatform();

        try {
          // Initialize platform-agnostic MIDI service
          const midiService = new MidiService({
            onDeviceConnected: async (device) => {
              console.log("[iOS] MIDI device connected:", device.name, device.id);

              // Only handle output devices (destinations we can send to)
              if (device.type !== 'output' && device.type !== 'both') return;

              // Use the output endpoint ID for sending MIDI
              const outputEndpointId = (device as any).outputId || device.id;
              const uniqueId = device.id;

              const state = get();
              const existingIndex = state.devices.findIndex((d) => d.uniqueId === uniqueId);

              if (existingIndex !== -1) {
                // Device reconnected - update state and flash green
                console.log(`[iOS] Device reconnected: ${device.name}`);

                // Connect to CoreMIDI
                try {
                  await midiService.connectDevice(outputEndpointId);
                  console.log(`[iOS] Connected device: ${device.name} (${outputEndpointId})`);
                } catch (err) {
                  console.error(`[iOS] Failed to connect device ${device.name}:`, err);
                }

                set((draft) => {
                  draft.devices[existingIndex].state = "connected";
                  draft.devices[existingIndex].productId = outputEndpointId;
                  draft.devices[existingIndex].enabled = true;
                  draft.devices[existingIndex].flashState = "green";

                  // Clear flash after 250ms
                  setTimeout(() => {
                    set((draft) => {
                      if (draft.devices[existingIndex]) {
                        draft.devices[existingIndex].flashState = "none";
                      }
                    });
                  }, 250);
                });
              } else {
                // New device - add it
                console.log(`[iOS] New device connected: ${device.name}`);

                const persistedDevices = loadPersistedDevices();
                const persistedDevice = persistedDevices?.find((d: any) => d.uniqueId === uniqueId);

                let deviceCCs;
                let deviceProfiles;
                let deviceActiveProfileId;
                let devicePattern;

                if (persistedDevice && persistedDevice.cc) {
                  deviceCCs = persistedDevice.cc;
                  deviceProfiles = persistedDevice.profiles || [];
                  deviceActiveProfileId = persistedDevice.activeProfileId || "";
                  devicePattern = persistedDevice.pattern || getDefaultPatternForDevice('MIDI');
                } else {
                  const hardwareProfile = getDefaultHardwareProfile();
                  const initialCCs = getBasicCCs();
                  deviceCCs = applyProfileToCCs(initialCCs, hardwareProfile);
                  deviceProfiles = [hardwareProfile];
                  deviceActiveProfileId = hardwareProfile.id;
                  devicePattern = getDefaultPatternForDevice('MIDI');
                }

                // Connect to CoreMIDI before adding to store
                try {
                  await midiService.connectDevice(outputEndpointId);
                  console.log(`[iOS] Connected device: ${device.name} (${outputEndpointId})`);
                } catch (err) {
                  console.error(`[iOS] Failed to connect device ${device.name}:`, err);
                  return; // Don't add device if connection failed
                }

                const newDevice: MidiDevice = {
                  label: device.name,
                  uniqueId: uniqueId,
                  productId: outputEndpointId,
                  cc: deviceCCs,
                  profiles: deviceProfiles,
                  activeProfileId: deviceActiveProfileId,
                  enabled: true,
                  active: persistedDevice?.active ?? getDefaultActiveState(true),
                  state: "connected",
                  panelOpen: persistedDevice?.panelOpen ?? false,
                  flashState: "green",
                  pattern: devicePattern,
                };

                set((draft) => {
                  // Prepend new device (after virtual devices)
                  const virtualDevices = draft.devices.filter(d => !d.productId);
                  const midiDevices = draft.devices.filter(d => d.productId);
                  draft.devices = [...virtualDevices, newDevice, ...midiDevices];

                  // Clear flash after 250ms
                  setTimeout(() => {
                    set((draft) => {
                      const index = draft.devices.findIndex((d) => d.uniqueId === uniqueId);
                      if (index !== -1) {
                        draft.devices[index].flashState = "none";
                      }
                    });
                  }, 250);
                });

                // Save the updated devices list
                saveDevicesToStorage(get().devices);
              }
            },
            onDeviceDisconnected: (device) => {
              console.log("[iOS] MIDI device disconnected:", device.name, device.id);

              // Only handle output devices
              if (device.type !== 'output' && device.type !== 'both') return;

              const uniqueId = device.id;
              const state = get();
              const existingIndex = state.devices.findIndex((d) => d.uniqueId === uniqueId);

              if (existingIndex !== -1) {
                // Stop and dispose the NoteGenerator for this device if it exists
                const noteGen = state.noteGenerators[existingIndex];
                if (noteGen) {
                  console.log(`[iOS] Stopping NoteGenerator for device ${existingIndex}`);
                  noteGen.stop();
                  noteGen.dispose();

                  set((draft) => {
                    delete draft.noteGenerators[existingIndex];
                  });
                }

                // Mark device as disconnected and flash red
                set((draft) => {
                  draft.devices[existingIndex].state = "disconnected";
                  draft.devices[existingIndex].enabled = false;
                  draft.devices[existingIndex].productId = undefined;
                  draft.devices[existingIndex].flashState = "red";

                  // Clear flash after 250ms
                  setTimeout(() => {
                    set((draft) => {
                      if (draft.devices[existingIndex]) {
                        draft.devices[existingIndex].flashState = "none";
                      }
                    });
                  }, 250);
                });
              }
            },
            onError: (error) => {
              console.error("MIDI error:", error);
              set({ midiError: error.message });
            }
          });

          const initialized = await midiService.initialize();

          if (!initialized) {
            let errorMsg: string;
            if (platform === 'ios') {
              errorMsg = "Failed to initialize CoreMIDI on iOS.";
            } else if (!isMidiSupported()) {
              const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
              if (isSafari) {
                errorMsg = "Safari does not support Web MIDI. Please use Chrome, Edge, or Opera.";
              } else {
                errorMsg = "Web MIDI is not supported in this browser. Please use Chrome, Edge, or Opera.";
              }
            } else {
              errorMsg = "Failed to initialize MIDI.";
            }

            console.warn(errorMsg);
            set({ midiError: errorMsg, midiReady: true, isInitializing: false });
            return;
          }

          // Store the MIDI service instance
          set({ midiService });

          console.log(`MIDI enabled on ${platform} via ${platform === 'ios' ? 'CoreMIDI' : 'WebMIDI'}`);

          // Handle iOS CoreMIDI device initialization
          if (platform === 'ios') {
            const devices = await midiService.getDevices();
            console.log('[iOS] Found MIDI devices:', devices);

            const persistedDevices = loadPersistedDevices();
            const realDevices: MidiDevice[] = [];

            // Process each detected CoreMIDI device
            devices.forEach((device) => {
              // Only add output devices (destinations we can send to)
              if (device.type === 'output' || device.type === 'both') {
                // Use the output endpoint ID for sending MIDI
                // For "both" devices, (device as any).outputId is the destination (where we send MIDI)
                // For "output" only devices, device.id is the destination
                console.log(`[iOS] Processing device: ${device.name}, type: ${device.type}, id: ${device.id}, outputId: ${(device as any).outputId}`);
                const outputEndpointId = (device as any).outputId || device.id;
                const uniqueId = device.id;
                console.log(`[iOS] Using outputEndpointId: ${outputEndpointId} for device: ${device.name}`);
                const persistedDevice = persistedDevices?.find((d: any) => d.uniqueId === uniqueId);

                // Check if device needs profile migration
                const needsProfileMigration = persistedDevice &&
                  persistedDevice.cc &&
                  !persistedDevice.cc.some((cc: any) => cc.scramble === true);

                let deviceCCs;
                let deviceProfiles;
                let deviceActiveProfileId;
                let devicePattern;

                if (persistedDevice && !needsProfileMigration && persistedDevice.cc) {
                  deviceCCs = persistedDevice.cc;
                  deviceProfiles = persistedDevice.profiles || [];
                  deviceActiveProfileId = persistedDevice.activeProfileId || "";
                  devicePattern = persistedDevice.pattern || getDefaultPatternForDevice('MIDI');
                } else {
                  const hardwareProfile = getDefaultHardwareProfile();
                  const initialCCs = getBasicCCs();
                  deviceCCs = applyProfileToCCs(initialCCs, hardwareProfile);
                  deviceProfiles = [hardwareProfile];
                  deviceActiveProfileId = hardwareProfile.id;
                  devicePattern = getDefaultPatternForDevice('MIDI');
                }

                // Migration: If active is undefined but enabled was false (old data), migrate to active: false
                // enabled should always be true for connected devices (it's auto-managed)
                // For new devices (no persisted data), default to false (muted)
                const migratedActive = persistedDevice?.active ??
                                       (persistedDevice?.enabled === false ? false : getDefaultActiveState(true));

                console.log(`[iOS] Creating device ${device.name} with productId: ${outputEndpointId}`);
                const midiDevice: MidiDevice = {
                  label: device.name,
                  uniqueId: uniqueId,
                  productId: outputEndpointId, // Use output endpoint ID for MIDI sending
                  cc: deviceCCs,
                  profiles: deviceProfiles,
                  activeProfileId: deviceActiveProfileId,
                  enabled: true, // Always true for connected devices (auto-managed)
                  active: migratedActive, // Migrated from old enabled value, or default to muted for new devices
                  state: "connected",
                  panelOpen: (persistedDevice as any)?.panelOpen ?? false,
                  flashState: "none",
                  pattern: devicePattern,
                };

                realDevices.push(midiDevice);
              }
            });

            console.log(`[iOS] Processed ${realDevices.length} MIDI output devices`);

            // Connect each device to CoreMIDI
            for (const device of realDevices) {
              try {
                await midiService.connectDevice(device.productId!);
                console.log(`[iOS] Connected device: ${device.label} (${device.productId})`);
              } catch (err) {
                console.error(`[iOS] Failed to connect device ${device.label}:`, err);
              }
            }

            // Keep virtual devices and add real devices
            set((draft) => {
              const virtualDevices = draft.devices.filter(d => !d.productId);
              draft.devices = [...virtualDevices, ...realDevices];
              console.log(`[iOS] Total devices: ${draft.devices.length} (${virtualDevices.length} virtual + ${realDevices.length} MIDI)`);
            });

            // Save to storage
            saveDevicesToStorage(get().devices);
          }

          // For backward compatibility, still enable WebMidi on non-iOS platforms
          if (platform !== 'ios') {
            await WebMidi.enable();
            console.log(`WebMidi enabled on ${getPlatform()}`, WebMidi.inputs, WebMidi.outputs);

            // Intercept all MIDI output playNote calls for global logging
            // Store original methods to avoid double-wrapping
            WebMidi.outputs.forEach((output) => {
              // Only wrap if not already wrapped
              if (!(output.playNote as any).__wrapped) {
                const originalPlayNote = output.playNote.bind(output);
                output.playNote = function(note: any, options?: any) {
                  console.log(`🎹 [MIDI OUT → ${output.name}] ${note}`, options);
                  return originalPlayNote(note, options);
                };
                (output.playNote as any).__wrapped = true;
              }
            });

            // Setup device listeners
            WebMidi.addListener("connected", (e) => {
              console.log("MIDI device connected:", e.port.name, e.port.id);
              get().handleDeviceConnected(e.port);

              // Also intercept playNote for newly connected devices
              const output = e.port;
              if (output.type === 'output' && output.playNote && !(output.playNote as any).__wrapped) {
                const originalPlayNote = output.playNote.bind(output);
                output.playNote = function(note: any, options?: any) {
                  console.log(`🎹 [MIDI OUT → ${output.name}] ${note}`, options);
                  return originalPlayNote(note, options);
                };
                (output.playNote as any).__wrapped = true;
              }
            });

            WebMidi.addListener("disconnected", (e) => {
              console.log("MIDI device disconnected:", e.port.name, e.port.id);
              get().handleDeviceDisconnected(e.port);
            });

            // Initial device list update
            get().updateDeviceList();
          }

          set({ midiError: null, midiReady: true, isInitializing: false, initialized: true });
        } catch (err) {
          const errorMsg = getMidiErrorMessage(err instanceof Error ? err : undefined);
          console.error("WebMidi could not be enabled.", err);
          set({ midiError: errorMsg, midiReady: true, isInitializing: false });
        }
      },

      startPlayback: async (config: NoteGeneratorConfig, callbacks: any) => {
        console.log("🎬 [PLAYBACK] startPlayback called");
        console.log("🎬 [PLAYBACK] Config:", {
          bpm: config.bpm,
          pattern: config.pattern,
          patternPartsKeys: Object.keys(config.patternParts || {}),
        });

        const state = get();
        console.log("🎬 [PLAYBACK] Current state:", {
          devicesCount: state.devices.length,
          synthsCount: Object.keys(state.synths).length,
          synthReady: state.synthReady,
        });

        // NOTE: Auto-select logic has been moved to App.tsx where user explicitly presses Play
        // This function should NOT auto-select, as it's called in many contexts (scramble, BPM change, etc.)

        // Ensure synths are created for all virtual devices before starting playback
        console.log("🎬 [PLAYBACK] Ensuring synths for all virtual devices...");
        for (let i = 0; i < state.devices.length; i++) {
          const device = state.devices[i];
          console.log(`🎬 [PLAYBACK] Device ${i}: ${device.label} - enabled: ${device.enabled}, productId: ${device.productId}, synthType: ${device.synthType}`);
          // Only create synths for virtual devices (no productId means virtual)
          if (device.enabled && !device.productId && device.synthType) {
            console.log(`🎬 [PLAYBACK] Creating synth for virtual device ${i}`);
            await state.ensureSynthForDevice(i);
            console.log(`[SYNTH] Ensured synth for device ${i} (${device.label}, ${device.synthType})`);
          }
        }
        console.log("🎬 [PLAYBACK] All synths ensured");

        // Create a map of NoteGenerators - one per enabled device
        const noteGenerators: Record<number, NoteGenerator> = {};

        // Get BPM and swing from config (global timing)
        const bpm = config.bpm;
        const swing = config.swing;

        // Calculate pattern lengths and LCM for bar synchronization
        const patternLengths: number[] = [];
        const patternPartsMap: Record<number, number> = {}; // Map device index to pattern parts count

        for (let i = 0; i < state.devices.length; i++) {
          const device = state.devices[i];
          if (!device.enabled || !device.pattern) {
            continue;
          }

          // Count the number of parts in the pattern sequence (e.g., ABCB = 4, AABAAC = 6)
          const patternPartsCount = device.pattern.pattern.sequence.length;
          patternLengths.push(patternPartsCount);
          patternPartsMap[i] = patternPartsCount;
        }

        // Import lcmArray for calculating sync cycle
        const { lcmArray } = await import('~/lib/math/lcm');
        const syncCycleLengthParts = patternLengths.length > 0 ? lcmArray(patternLengths) : 0;
        if (syncCycleLengthParts > 0) {
          console.log(`🔄 [BAR SYNC] Pattern lengths (parts): [${patternLengths.join(', ')}], LCM sync cycle: ${syncCycleLengthParts} parts`);
        }

        // Track pattern completions for each device
        const patternCompletions: Record<number, number> = {};

        // Create NoteGenerator for each enabled device with its own pattern
        for (let i = 0; i < state.devices.length; i++) {
          const device = state.devices[i];
          console.log(`[DEVICE ${i}] ${device.label} - enabled: ${device.enabled}, active: ${device.active}, hasPattern: ${!!device.pattern}`);
          if (!device.enabled || !device.pattern) {
            console.log(`[DEVICE ${i}] Skipping - enabled: ${device.enabled}, pattern: ${!!device.pattern}`);
            continue;
          }

          const deviceConfig: NoteGeneratorConfig = {
            bpm,
            swing,
            pattern: device.pattern.pattern.sequence,
            patternParts: device.pattern.patternParts,
          };

          // Initialize completion counter for this device
          patternCompletions[i] = 0;

          // Create device-specific callbacks that know their device index
          const deviceIndex = i;
          const deviceCallbacks = {
            ...callbacks,
            // Only play internal synth for virtual synths (devices without productId)
            // Real MIDI devices send to hardware, so they shouldn't play internal synth
            shouldPlaySynth: () => {
              const currentState = get();
              const currentDevice = currentState.devices[deviceIndex];
              // Only play synth if: 1) no MIDI output (virtual device), 2) device is enabled, 3) device is active (not muted)
              return !currentDevice?.productId && currentDevice?.enabled && currentDevice?.active;
            },
            onPatternComplete: () => {
              // Each device loops independently
              // Log pattern completion for debugging
              console.log(`🔄 [PATTERN] Device ${deviceIndex} (${device.label}) completed its pattern`);

              // Optional: Track completions for future features like synchronized moments
              // but don't force reset other devices
              patternCompletions[deviceIndex]++;

              // Progress bar reset is now handled by the beat counter loop
              // Each device continues looping on its own
              // The NoteGenerator handles its own looping automatically
            },
            onNotePlayed: (note: string, partIndex: number, noteIndexInPart: number) => {
              const currentState = get(); // Get fresh state each time
              const currentDevice = currentState.devices[deviceIndex];
              const devicePattern = currentDevice?.pattern;

              // Track current note for this device
              set((draft) => {
                draft.deviceCurrentNotes[deviceIndex] = note;
              });

              // Log which device is playing this note
              console.log(`🎵 [DEVICE ${deviceIndex}: ${currentDevice?.label}] Playing ${note}`);

              // Call original callback for UI updates (only for device 0)
              if (deviceIndex === 0 && callbacks.onNotePlayed) {
                callbacks.onNotePlayed(note, partIndex, noteIndexInPart);
              }

              // Send MIDI note only to this specific device
              if (devicePattern) {
                const partName = devicePattern.pattern.sequence[partIndex] as keyof typeof devicePattern.patternParts;
                const part = devicePattern.patternParts[partName];
                if (part && part[noteIndexInPart]) {
                  currentState.sendMidiNoteToDevice(deviceIndex, note, part[noteIndexInPart].duration);
                }
              }
            },
            onRest: (partIndex: number, noteIndexInPart: number) => {
              // Clear current note for this device
              set((draft) => {
                draft.deviceCurrentNotes[deviceIndex] = null;
              });

              // Only call original callback for device 0
              if (deviceIndex === 0 && callbacks.onRest) {
                callbacks.onRest(partIndex, noteIndexInPart);
              }
            },
            onPartChange: (partName: string, partIndex: number) => {
              const currentState = get(); // Get fresh state each time

              // Update pattern position for this device (all devices, not just device 0)
              currentState.setDevicePatternPosition(deviceIndex, partIndex);

              // Only call original callback for device 0
              if (deviceIndex === 0 && callbacks.onPartChange) {
                callbacks.onPartChange(partName, partIndex);
              }
            },
            // Keyboard tracking: adjust filter cutoff based on note pitch
            // This reduces buzzing at lower pitches by rolling off high frequencies
            onBeforeNotePlay: (note: string) => {
              const currentState = get();
              const filter = currentState.filters[deviceIndex];
              const currentDevice = currentState.devices[deviceIndex];

              if (filter && currentDevice) {
                // Get base filter cutoff from CC74 (filter cutoff) or use default
                // CC74 value 0-127 maps to 200-20000 Hz exponentially
                const cc74 = currentDevice.cc?.find(cc => cc.number === 74);
                const cc74Value = cc74?.value ?? 64; // Default to mid-range

                // Scale CC74 to frequency (exponential curve matching ccMapping.ts)
                const baseCutoff = 200 + (20000 - 200) * Math.pow(cc74Value / 127, 2);

                // Apply keyboard tracking with 0.5 amount (moderate tracking)
                // At lower pitches, filter closes down; at higher pitches, it opens up
                applyKeyboardTracking(filter, note, baseCutoff, 0.5);
              }
            },
          };

          // Calculate total pattern length in quarter note beats (not notes)
          const beatsPerBar = device.pattern.beatsPerBar || 4;
          const totalBeats = device.pattern.pattern.sequence.length * beatsPerBar;

          console.log(`🎵 [DEVICE ${i}: ${device.label}] Pattern: ${device.pattern.pattern.name}, Total beats: ${totalBeats} (${device.pattern.pattern.sequence.length} parts × ${beatsPerBar} beats/bar)`);

          // Get the synth for this device - ensure each device gets its own synth instance
          const deviceSynth = state.synths[i] || await state.ensureSynthForDevice(i);

          const noteGenerator = new NoteGenerator(deviceSynth, deviceConfig, deviceCallbacks, state.envelopeGate);
          await noteGenerator.start(); // Starts loop but NOT Transport
          noteGenerators[i] = noteGenerator;
        }

        // Legacy noteGenerator is deprecated - only used if no multi-device generators
        // (This maintains backward compatibility for edge cases)
        let legacyNoteGenerator = null;
        if (Object.keys(noteGenerators).length === 0) {
          const device = state.devices[0]; // Use device 0 for legacy fallback
          const legacyConfig = device?.pattern ? {
            bpm,
            swing,
            pattern: device.pattern.pattern.sequence,
            patternParts: device.pattern.patternParts,
          } : config;
          // Use device 0's synth for legacy generator
          const fallbackSynth = state.synths[0] || await state.ensureSynthForDevice(0);
          legacyNoteGenerator = new NoteGenerator(fallbackSynth, legacyConfig, callbacks, state.envelopeGate);
          await legacyNoteGenerator.start(); // Starts loop but NOT Transport
        }

        set((draft) => {
          draft.noteGenerator = legacyNoteGenerator;
          draft.noteGenerators = noteGenerators;
        });

        // CENTRALIZED TRANSPORT CONTROL: Start Transport ONCE after all devices are ready
        // This ensures all devices start in sync
        console.log(`🎼 [TRANSPORT] Starting Transport with ${Object.keys(noteGenerators).length} device(s)`);

        // ANTI-POP: Fade in synth volumes to prevent clicking/popping when playback starts
        // Set all synths to silent before starting, then ramp to target volume
        // This applies to both Tone.js synths and WASM synths
        const currentState = get();
        Object.keys(currentState.synths).forEach((indexStr) => {
          const synth = currentState.synths[parseInt(indexStr)];
          if (synth && synth.volume) {
            // Set volume to silent before starting
            synth.volume.value = ANTI_POP_INITIAL_VOLUME;
            console.log(`🔊 [ANTI-POP] Setting synth ${indexStr} volume to ${ANTI_POP_INITIAL_VOLUME}dB before start`);
          }
        });

        Tone.getTransport().start();
        const transportStartTime = Tone.getTransport().seconds;
        console.log(`🎼 [TRANSPORT] Transport started at position ${transportStartTime}s`);

        // Fade in synth volumes to prevent pops
        Object.keys(currentState.synths).forEach((indexStr) => {
          const synth = currentState.synths[parseInt(indexStr)];
          if (synth && synth.volume) {
            // Use rampTo for smooth volume transition (works for both Tone.js and WASM synths)
            synth.volume.rampTo(ANTI_POP_TARGET_VOLUME, ANTI_POP_FADE_IN_TIME, Tone.now());
            console.log(`🔊 [ANTI-POP] Ramping synth ${indexStr} volume from ${ANTI_POP_INITIAL_VOLUME}dB to ${ANTI_POP_TARGET_VOLUME}dB over ${ANTI_POP_FADE_IN_TIME * 1000}ms`);
          }
        });

        // Create global beat counter loop for beat-synchronized scrambling and events system
        // This runs on every quarter note (beat) and increments the global beat counter
        // Note: Beats are 1-indexed (Beat 1, Beat 2, etc.) as per musical convention
        const beatCounterLoop = new Tone.Loop(() => {
          set((draft) => {
            draft.globalBeatCounter++;
            console.log(`🎵 [BEAT] Global beat: ${draft.globalBeatCounter}`);

            draft.devices.forEach((device, deviceIndex) => {
              if (device.enabled && device.pattern) {
                const beatsPerBar = device.pattern.beatsPerBar || 4;
                const totalBeatsPerStructure = device.pattern.pattern.sequence.length * beatsPerBar;

                // Calculate current beat within this device's pattern cycle (1-indexed)
                // Beat 1 = first beat, Beat 16 = last beat (for 16-beat pattern)
                const beatInCycle = ((draft.globalBeatCounter - 1) % totalBeatsPerStructure) + 1;

                // Progress percentage: static fill based on current beat position (no smooth transitions)
                // Updates instantly on each beat
                const progress = (beatInCycle / totalBeatsPerStructure) * 100;

                // No transition - instant update
                draft.deviceTransitionDurations[deviceIndex] = 0;
                draft.deviceProgressPercentages[deviceIndex] = Math.max(0, Math.min(100, progress));

                console.log(`📊 [BEAT PROGRESS] Device ${deviceIndex}: Beat ${beatInCycle}/${totalBeatsPerStructure} = ${progress.toFixed(1)}%`);
              }
            });
          });
        }, "4n"); // "4n" = quarter note (1 beat)

        beatCounterLoop.start(0);

        // Store beat counter loop and transport start time in state
        set((draft) => {
          draft.globalBeatCounter = 0; // Reset to 0 on new playback
          draft.transportStartTime = transportStartTime;
          draft.beatCounterLoop = beatCounterLoop;
        });

        if (state.midiClockEnabled) {
          // Use AudioScheduler-based MIDI clock for drift-free timing
          try {
            const latencyConfig = getLatencyConfig(state.latencyPreset);
            const audioContext = getAudioContext();
            const audioScheduler = new AudioScheduler(audioContext, {
              lookAhead: latencyConfig.lookAheadSeconds,
            });
            audioScheduler.setBPM(config.bpm);
            audioScheduler.start();

            const audioMidiClock = new AudioMidiClock(audioScheduler, config.bpm, {
              latencyMs: latencyConfig.midiLatencyMs,
            });

            state.devices.forEach(device => {
              if (device.enabled && device.productId) {
                const output = WebMidi.outputs.find(o => o.id === device.productId);
                if (output) {
                  audioMidiClock.addOutput(output);
                }
              }
            });
            audioMidiClock.start();
            set({ audioMidiClock, audioScheduler, midiClockRunning: true });
            console.log(`[MIDI CLOCK] AudioMidiClock started (preset: ${state.latencyPreset})`);
          } catch (error) {
            // Fallback to legacy setInterval-based clock if AudioScheduler fails
            console.warn('[MIDI CLOCK] AudioMidiClock failed, falling back to legacy MidiClock:', error);
            const midiClock = new MidiClock(config.bpm);
            state.devices.forEach(device => {
              if (device.enabled && device.productId) {
                const output = WebMidi.outputs.find(o => o.id === device.productId);
                if (output) {
                  midiClock.addOutput(output);
                }
              }
            });
            midiClock.start();
            set({ midiClock, midiClockRunning: true });
          }
        }
      },

      stopPlayback: () => {
        const state = get();

        // CENTRALIZED TRANSPORT CONTROL: Stop Transport FIRST before stopping devices
        // This ensures clean shutdown and prevents race conditions
        console.log(`🎼 [TRANSPORT] Stopping Transport (was at position ${Tone.getTransport().seconds}s)`);
        Tone.getTransport().stop();
        Tone.getTransport().cancel(); // Cancel all scheduled events
        console.log(`🎼 [TRANSPORT] Transport stopped`);

        // Stop and dispose beat counter loop
        if (state.beatCounterLoop) {
          state.beatCounterLoop.stop();
          state.beatCounterLoop.dispose();
          console.log(`🎵 [BEAT] Beat counter loop stopped and disposed`);
        }

        // Stop and dispose all NoteGenerators in the map
        Object.values(state.noteGenerators).forEach(generator => {
          if (generator) {
            generator.stop(); // Now only stops loop, not Transport
            generator.dispose();
          }
        });

        // Also stop legacy noteGenerator for backward compatibility
        if (state.noteGenerator) {
          state.noteGenerator.stop(); // Now only stops loop, not Transport
          state.noteGenerator.dispose();
        }

        // Reset all device progress bars to 0%
        const resetProgressPercentages: Record<number, number> = {};
        const resetCurrentNotes: Record<number, string | null> = {};
        state.devices.forEach((_, index) => {
          resetProgressPercentages[index] = 0;
          resetCurrentNotes[index] = null;
        });

        set({
          noteGenerator: null,
          noteGenerators: {},
          beatCounterLoop: null,
          globalBeatCounter: 0,
          transportStartTime: null,
          currentNote: null,
          currentInfo: "",
          lastMidiNote: null,
          deviceProgressPercentages: resetProgressPercentages,
          deviceCurrentNotes: resetCurrentNotes,
        });

        // Stop AudioMidiClock and AudioScheduler (new timing system)
        if (state.audioMidiClock) {
          state.audioMidiClock.dispose();
        }
        if (state.audioScheduler) {
          state.audioScheduler.dispose();
        }

        // Stop legacy MidiClock (fallback)
        if (state.midiClock && state.midiClockRunning) {
          state.midiClock.stop();
        }

        set({ midiClockRunning: false, audioMidiClock: null, audioScheduler: null, midiClock: null });
      },

      updateConfig: (config: NoteGeneratorConfig) => {
        const state = get();

        // Update all NoteGenerators in the map
        Object.values(state.noteGenerators).forEach(generator => {
          if (generator) {
            generator.updateConfig(config);
          }
        });

        // Also update legacy noteGenerator for backward compatibility
        if (state.noteGenerator) {
          state.noteGenerator.updateConfig(config);
        }

        // Update AudioMidiClock BPM (new timing system)
        if (state.audioMidiClock && state.midiClockRunning) {
          state.audioMidiClock.setBPM(config.bpm);
        }
        // Update legacy MidiClock BPM (fallback)
        if (state.midiClock && state.midiClockRunning) {
          state.midiClock.setBPM(config.bpm);
        }
      },

      updateConfigContinuous: (config: NoteGeneratorConfig) => {
        const state = get();

        // Update all NoteGenerators in the map
        Object.values(state.noteGenerators).forEach(generator => {
          if (generator) {
            generator.updateConfigContinuous(config);
          }
        });

        // Also update legacy noteGenerator for backward compatibility
        if (state.noteGenerator) {
          state.noteGenerator.updateConfigContinuous(config);
        }

        // Update AudioMidiClock BPM (new timing system)
        if (state.audioMidiClock && state.midiClockRunning) {
          state.audioMidiClock.setBPM(config.bpm);
        }
        // Update legacy MidiClock BPM (fallback)
        if (state.midiClock && state.midiClockRunning) {
          state.midiClock.setBPM(config.bpm);
        }
      },

      updateCallbacks: (callbacks: any) => {
        const state = get();

        // Update callbacks for all NoteGenerators in the map
        Object.values(state.noteGenerators).forEach(generator => {
          if (generator) {
            generator.updateCallbacks(callbacks);
          }
        });

        // Also update legacy noteGenerator for backward compatibility
        if (state.noteGenerator) {
          state.noteGenerator.updateCallbacks(callbacks);
        }
      },

      clearCurrentNote: () => {
        set((draft) => {
          draft.currentNote = null;
          draft.currentInfo = "";
          draft.noteTrigger = 0;
        });
      },

      // Pattern management functions
      setDevicePattern: (deviceIndex, updater) =>
        set((draft) => {
          const device = draft.devices[deviceIndex];
          if (device && device.pattern) {
            // Capture old pattern structure before update
            const oldPatternStructure = device.pattern.pattern.name;

            // Apply the update
            updater(device.pattern);

            // Check if pattern structure changed
            const newPatternStructure = device.pattern.pattern.name;
            const structureChanged = oldPatternStructure !== newPatternStructure;

            saveDevicesToStorage(draft.devices);

            // If pattern structure changed during playback, restart all patterns to stay synchronized
            if (structureChanged) {
              const isPlaying = draft.beatCounterLoop !== null;

              if (isPlaying) {
                console.log(`🔄 [PATTERN] Structure changed from "${oldPatternStructure}" to "${newPatternStructure}" - restarting playback`);

                // Reset global playback position and beat counter
                draft.currentPlaybackPosition = 0;
                draft.globalBeatCounter = 0;

                // Reset all generators to position 0
                Object.entries(draft.noteGenerators).forEach(([index, generator]) => {
                  const devIndex = parseInt(index);
                  const dev = draft.devices[devIndex];

                  if (generator && dev?.pattern) {
                    // Update the generator's config with current pattern (especially for the changed device)
                    if (devIndex === deviceIndex) {
                      // Convert Immer drafts to plain objects to avoid revoked proxy errors
                      const plainPattern = JSON.parse(JSON.stringify(dev.pattern.pattern.sequence));
                      const plainPatternParts = JSON.parse(JSON.stringify(dev.pattern.patternParts));

                      generator.updateConfigContinuous({
                        pattern: plainPattern,
                        patternParts: plainPatternParts,
                      });
                    }

                    // Reset to start position
                    generator.resetPosition();

                    // Reset state tracking
                    draft.devicePatternPositions[devIndex] = 0;
                    draft.deviceProgressPercentages[devIndex] = 0;
                  }
                });

                console.log(`✅ [PATTERN] All devices reset to position 0`);
              }
            }
          }
        }),

      setDevicePatternPosition: (deviceIndex, position) =>
        set((draft) => {
          draft.devicePatternPositions[deviceIndex] = position;
        }),

      setDeviceProgressPercentage: (deviceIndex, percentage) =>
        set((draft) => {
          draft.deviceProgressPercentages[deviceIndex] = Math.max(0, Math.min(100, percentage));
        }),

      getDevicePattern: (deviceIndex) => {
        const device = get().devices[deviceIndex];
        return device?.pattern;
      },

      scrambleDevicePattern: (deviceIndex) => {
        set((draft) => {
          const device = draft.devices[deviceIndex];
          if (!device || !device.pattern) {
            return;
          }

          const pattern = device.pattern;
          const toggles = pattern.scrambleToggles;

          // Scramble structure
          if (toggles.scrambleStructure) {
            pattern.pattern = PATTERNS[Math.floor(Math.random() * PATTERNS.length)];
          }

          // Scramble root note
          if (toggles.scrambleRootNote) {
            pattern.rootNote = musicData.rootNotes[Math.floor(Math.random() * musicData.rootNotes.length)];
          }

          // Scramble mode FIRST
          const oldMode = pattern.scaleMode;
          const newMode = toggles.scrambleMode
            ? (Math.random() > 0.5 ? 'Major' : 'Minor')
            : pattern.scaleMode;

          pattern.scaleMode = newMode;

          // If mode changed OR scale scrambling is enabled, scramble scale to match new mode
          const modeChanged = newMode !== oldMode;
          if (toggles.scrambleScale || modeChanged) {
            // Always filter scales by the current mode to prevent Major/Minor mismatch
            const availableScales = musicData.scales.filter(s => s.mode === newMode);
            if (availableScales.length > 0) {
              pattern.scale = availableScales[Math.floor(Math.random() * availableScales.length)];
            } else {
              console.warn(`[SCRAMBLE] No scales available for mode ${newMode}, keeping current scale`);
            }
          }

          // Scramble generative parameters using min/max bounds
          if (toggles.scrambleSpace) {
            const min = pattern.spaceMin;
            const max = pattern.spaceMax;
            pattern.space = Math.floor(Math.random() * (max - min + 1)) + min;
          }
          if (toggles.scrambleDissonance) {
            const min = pattern.dissonanceMin;
            const max = pattern.dissonanceMax;
            pattern.dissonance = Math.floor(Math.random() * (max - min + 1)) + min;
          }
          if (toggles.scrambleSpread) {
            const min = pattern.spreadMin;
            const max = pattern.spreadMax;
            pattern.spread = Math.floor(Math.random() * (max - min + 1)) + min;
          }
          if (toggles.scrambleRange) {
            const min = pattern.rangeMin;
            const max = pattern.rangeMax;
            pattern.range = Math.floor(Math.random() * (max - min + 1)) + min;
          }
          if (toggles.scrambleTonicGravity) {
            const min = pattern.tonicGravityMin ?? -6;
            const max = pattern.tonicGravityMax ?? 6;
            pattern.tonicGravity = Math.floor(Math.random() * (max - min + 1)) + min;
          }

          // Ensure scale is valid before generating pattern parts
          if (!pattern.scale || !pattern.scale.progression) {
            const fallbackScales = musicData.scales.filter(s => s.mode === pattern.scaleMode);
            if (fallbackScales.length > 0) {
              const fallbackScale = fallbackScales[0];
              pattern.scale = fallbackScale;
              console.warn(`[SCRAMBLE] Scale was invalid, using fallback scale: ${fallbackScale.label}`);
            } else {
              throw new Error(`[SCRAMBLE] No scales available for mode ${pattern.scaleMode}`);
            }
          }

          // Generate new pattern parts
          // Get global noteLength from useNotesStore
          const globalNoteLength = useNotesStore.getState().noteLength;
          const chromatic = musicData.rootNotes.map((n) => n.note);
          const parts = generatePatternParts(pattern.pattern.sequence, {
            rootNote: pattern.rootNote.note,
            scale: pattern.scale.progression,
            bpm: 108, // Placeholder - BPM is global
            noteLength: globalNoteLength.length,
            timeSignatureBeats: 4, // Always 4/4 for now
            space: pattern.space,
            dissonance: pattern.dissonance,
            spread: pattern.spread,
            range: pattern.range,
            chromatic,
            tonicGravity: pattern.tonicGravity ?? 0,
          });

          pattern.patternParts = parts as any; // Type assertion needed due to Immer draft

          saveDevicesToStorage(draft.devices);

          // TEMPORARY FIX: Due to timing bugs, restart ALL patterns when any pattern is scrambled
          // TODO: Fix the underlying timing synchronization issues to allow independent pattern updates
          const isPlaying = draft.beatCounterLoop !== null;

          if (isPlaying) {
            console.log(`🔄 [SCRAMBLE] Restarting all device patterns to avoid timing bugs`);

            // Reset global playback position and beat counter
            draft.currentPlaybackPosition = 0;
            draft.globalBeatCounter = 0;

            // Reset all generators to position 0
            Object.entries(draft.noteGenerators).forEach(([index, generator]) => {
              const devIndex = parseInt(index);
              const dev = draft.devices[devIndex];

              if (generator && dev?.pattern) {
                // Update the generator's config with current pattern (in case it's the scrambled one)
                if (devIndex === deviceIndex) {
                  // Convert Immer drafts to plain objects to avoid revoked proxy errors
                  const plainPattern = JSON.parse(JSON.stringify(dev.pattern.pattern.sequence));
                  const plainPatternParts = JSON.parse(JSON.stringify(dev.pattern.patternParts));

                  generator.updateConfigContinuous({
                    pattern: plainPattern,
                    patternParts: plainPatternParts,
                  });
                }

                // Reset to start position
                generator.resetPosition();

                // Reset state tracking
                draft.devicePatternPositions[devIndex] = 0;
                draft.deviceProgressPercentages[devIndex] = 0;
              }
            });
          } else {
            // NOT PLAYING: Just update the scrambled device's generator and reset its position
            const generator = draft.noteGenerators[deviceIndex];
            if (generator) {
              console.log(`🔄 [SCRAMBLE] Device ${deviceIndex} not playing, resetting to start`);

              // Convert Immer drafts to plain objects to avoid revoked proxy errors
              const plainPattern = JSON.parse(JSON.stringify(pattern.pattern.sequence));
              const plainPatternParts = JSON.parse(JSON.stringify(pattern.patternParts));

              // Update the generator's config with the new pattern
              generator.updateConfigContinuous({
                pattern: plainPattern,
                patternParts: plainPatternParts,
              });

              generator.resetPosition();

              // Reset only this device's pattern position
              draft.devicePatternPositions[deviceIndex] = 0;

              // Reset only this device's progress percentage
              draft.deviceProgressPercentages[deviceIndex] = 0;
            }
          }
        });
      },

      scrambleDeviceCCs: async (deviceIndex) => {
        const state = get();
        const device = state.devices[deviceIndex];

        if (!device) {
          console.log(`🎛️ [SCRAMBLE CCs] Invalid device index: ${deviceIndex}`);
          return;
        }

        console.log(`🎛️ [SCRAMBLE CCs] Starting CC scramble for ${device.label}`, {
          enabled: device.enabled,
          productId: device.productId,
          totalCCs: device.cc.length,
        });

        // Generate new random values for CCs with scramble: true within their min/max ranges
        const updatedCCs = device.cc.map((cc) => {
          if (cc.scramble) {
            const newValue = random.int(cc.min, cc.max);
            console.log(`🎛️ [SCRAMBLE CCs] CC${cc.number}: ${cc.value} → ${newValue} (range: ${cc.min}-${cc.max})`);
            return { ...cc, value: newValue };
          }
          return cc;
        });

        const scrambledCount = updatedCCs.filter(cc => cc.scramble).length;
        console.log(`🎛️ [SCRAMBLE CCs] Generated ${scrambledCount} new CC values`);

        // Update state with new CC values
        set((draft) => {
          draft.devices[deviceIndex].cc = updatedCCs;
          saveDevicesToStorage(draft.devices);
        });

        // Create updated device object with new CC values
        const updatedDevice = {
          ...device,
          cc: updatedCCs
        };

        // Apply CC mappings to virtual synths (Tone.js devices)
        if (device.synthType && !device.productId) {
          const synth = await state.ensureSynthForDevice(deviceIndex);
          const filter = state.getFilterForDevice(deviceIndex);

          if (!filter) {
            console.error(`🎛️ [SCRAMBLE CCs] Filter not available for ${device.label}`);
            return;
          }

          // Apply all scrambled CCs to the synth using the CC mapping
          // Filter to only CCs with known values (scrambling sets values, but TypeScript needs assurance)
          const knownCCs = updatedCCs.filter((cc): cc is MidiCC & { value: number } => cc.value !== null);
          applyMultipleCCsToSynth(knownCCs, synth, filter);
          console.log(`🎛️ [SCRAMBLE CCs] Applied ${scrambledCount} CCs to ${device.label} (${device.synthType})`);
        } else {
          // Send scrambled CCs to connected MIDI device
          console.log(`🎛️ [SCRAMBLE CCs] Sending ${scrambledCount} CCs to ${device.label}`);
          state.sendControlChanges(updatedDevice);
          console.log(`🎛️ [SCRAMBLE CCs] Complete`);
        }
      },

      cleanup: () => {
        const state = get();
        if (state.noteGenerator) {
          state.noteGenerator.dispose();
        }
        if (state.audioMidiClock) {
          state.audioMidiClock.dispose();
        }
        if (state.audioScheduler) {
          state.audioScheduler.dispose();
        }
        if (state.midiClock) {
          state.midiClock.stop();
        }
        // Dispose all synths
        Object.values(state.synths).forEach(synth => {
          synth?.dispose();
        });
        // Dispose all filters
        Object.values(state.filters).forEach(filter => {
          filter?.dispose();
        });
        // Dispose shared effects chain (stored in module variables)
        // Order matters: dispose in reverse order of signal chain
        if (sharedReverbInstance) {
          sharedReverbInstance.dispose();
          sharedReverbInstance = null;
        }
        if (sharedCompressorInstance) {
          sharedCompressorInstance.dispose();
          sharedCompressorInstance = null;
        }
        if (sharedLimiterInstance) {
          sharedLimiterInstance.dispose();
          sharedLimiterInstance = null;
        }
        console.log(`[SYNTH] Disposed shared effects chain (reverb, compressor, limiter)`);
      },

      // Update synth parameters for MIDI learn
      updateSynthParameter: (deviceIndex: number, parameter: string, value: number) => {
        const state = get();
        const synth = state.synths[deviceIndex];
        const device = state.devices[deviceIndex];

        if (!synth || !device) {
          console.warn(`[MIDI LEARN] No synth or device for index ${deviceIndex}`);
          return;
        }

        try {
          // Check synth type and update accordingly
          const synthType = device.synthType;

          switch (synthType) {
            case 'FMSynth':
              // FMSynth has all these properties
              if ('envelope' in synth) {
                switch (parameter) {
                  case 'attack':
                    (synth as any).envelope.attack = value;
                    break;
                  case 'decay':
                    (synth as any).envelope.decay = value;
                    break;
                  case 'sustain':
                    (synth as any).envelope.sustain = value;
                    break;
                  case 'release':
                    (synth as any).envelope.release = value;
                    break;
                  case 'harmonicity':
                    if ('harmonicity' in synth) {
                      (synth as any).harmonicity.value = value;
                    }
                    break;
                  case 'modulationIndex':
                    if ('modulationIndex' in synth) {
                      (synth as any).modulationIndex.value = value;
                    }
                    break;
                }
              }
              break;

            case 'MonoSynth':
              // MonoSynth has envelope but not FM parameters
              if ('envelope' in synth) {
                switch (parameter) {
                  case 'attack':
                    (synth as any).envelope.attack = value;
                    break;
                  case 'decay':
                    (synth as any).envelope.decay = value;
                    break;
                  case 'sustain':
                    (synth as any).envelope.sustain = value;
                    break;
                  case 'release':
                    (synth as any).envelope.release = value;
                    break;
                }
              }
              break;

            default:
              console.warn(`[MIDI LEARN] Unknown synth type: ${synthType}`);
          }
        } catch (error) {
          console.error(`[MIDI LEARN] Failed to update synth parameter:`, error);
        }
      },

      // Update filter parameters for MIDI learn
      updateFilterParameter: (deviceIndex: number, parameter: string, value: number) => {
        const filter = get().filters[deviceIndex];
        if (!filter) {
          console.warn(`[MIDI LEARN] No filter for device ${deviceIndex}`);
          return;
        }

        try {
          // Map parameter names to Tone.js filter properties
          switch (parameter) {
            case 'frequency':
              filter.frequency.value = value;
              break;
            case 'resonance':
              filter.Q.value = value;
              break;
            case 'envelope':
              // Envelope controls how much the filter opens/closes
              // We can modulate the frequency based on this
              const baseFreq = 200;
              const maxFreq = 5000;
              filter.frequency.value = baseFreq + (maxFreq - baseFreq) * value;
              break;
            default:
              console.warn(`[MIDI LEARN] Unknown filter parameter: ${parameter}`);
          }
        } catch (error) {
          console.error(`[MIDI LEARN] Failed to update filter parameter:`, error);
        }
      },

      exportProject: () => {
        const state = get();

        // Create project snapshot with only serializable state
        const project = {
          version: '1.0',
          route: '/', // Will be used later for navigation
          devices: state.devices.map(device => ({
            label: device.label,
            // enabled is NOT saved - it's calculated by the system based on device connection
            active: device.active,
            panelOpen: device.panelOpen,
            cc: device.cc,
            pattern: device.pattern,
            synthType: device.synthType,
            productId: device.productId,
            // Include device identity for reconnection
            vendorId: device.vendorId,
            uniqueId: device.uniqueId,
          })),
          timestamp: new Date().toISOString(),
        };

        return JSON.stringify(project, null, 2);
      },

      importProject: (projectJson: string) => {
        try {
          const project = JSON.parse(projectJson);

          // Validate project structure
          if (!project.version || !project.devices || !Array.isArray(project.devices)) {
            throw new Error('Invalid project format');
          }

          console.log('📂 [PROJECT IMPORT] Loading project:', {
            version: project.version,
            deviceCount: project.devices.length,
            timestamp: project.timestamp,
          });

          set((draft) => {
            // Merge imported devices with current devices
            // Match by uniqueId for hardware devices, by synthType for virtual devices
            project.devices.forEach((importedDevice: any) => {
              let deviceIndex = -1;

              if (importedDevice.productId) {
                // Hardware device - match by uniqueId or productId
                deviceIndex = draft.devices.findIndex(
                  d => d.uniqueId === importedDevice.uniqueId || d.productId === importedDevice.productId
                );
              } else if (importedDevice.synthType) {
                // Virtual device - match by synthType
                deviceIndex = draft.devices.findIndex(
                  d => d.synthType === importedDevice.synthType && !d.productId
                );
              }

              if (deviceIndex !== -1) {
                // Device exists - update its state
                const device = draft.devices[deviceIndex];
                device.active = importedDevice.active ?? device.active;
                device.panelOpen = importedDevice.panelOpen ?? device.panelOpen;
                device.cc = importedDevice.cc ?? device.cc;
                device.pattern = importedDevice.pattern ?? device.pattern;

                console.log(`✅ [PROJECT IMPORT] Updated device ${deviceIndex}: ${device.label}`);
              } else {
                console.warn(`⚠️ [PROJECT IMPORT] Device not found in current setup: ${importedDevice.label}`);
              }
            });

            // Save updated devices to localStorage
            saveDevicesToStorage(draft.devices);
          });

          console.log('✅ [PROJECT IMPORT] Project loaded successfully');
        } catch (error) {
          console.error('❌ [PROJECT IMPORT] Failed to import project:', error);
          throw error;
        }
      },
    };
  })
);
